import axios, { isAxiosError } from 'axios'
import { getUnixTime } from 'date-fns'
import { POST_TYPES, PRICING_MODELS } from '@/constants/post.js'

export default class ApiStore {
  rootStore = null

  #apiClient = null

  constructor(rootStore) {
    this.rootStore = rootStore
    this.#apiClient = axios.create({
      baseURL: import.meta.env.VITE_APP_BACKEND_URL,
      withCredentials: true,
    })
  }

  get affiliate() {
    return this.rootStore.affiliateStore
  }

  get auth() {
    return this.rootStore.authStore
  }

  get fingerprint() {
    return this.rootStore.fingerprintStore
  }

  get jitsi() {
    return this.rootStore.jitsiStore
  }

  get wallet() {
    return this.rootStore.walletStore
  }

  createCategory = async ({ description, name, published }) =>
    this.#callBackend('addCategory', { data: { categoryName: name, description, published } })

  createPost = async ({
    categoryId,
    description,
    durationMins,
    endTime,
    imagePreview,
    id,
    price,
    pricingModel,
    published,
    startTime,
    title,
    type,
    videoContent,
    videoPreview,
  }) => {
    const getBroadcast = () =>
      type === POST_TYPES.VIDEO || type === POST_TYPES.IMAGE || type === POST_TYPES.LIVESTREAM_JITSI

    const getStreamDevice = () => {
      if (type === POST_TYPES.VIDEO) return 'upload'
      if (type === POST_TYPES.IMAGE) return 'bundle'
      if (type === POST_TYPES.LIVESTREAM_OBS) return 'obs'

      return 'webcam'
    }

    const payload = {
      broadcast: getBroadcast(),
      description,
      email: this.auth.user.email,
      endTime: endTime ? getUnixTime(endTime) : null,
      id,
      name: this.wallet.address,
      nickname: this.auth.user.nickname,
      price: pricingModel === PRICING_MODELS.FREE ? 0 : price,
      promotion: published,
      startTime: startTime ? getUnixTime(startTime) : null,
      streamdevice: getStreamDevice(),
      sub: import.meta.env.VITE_JITSI_DOMAIN,
      subject: title,
      time: durationMins,
      type: pricingModel,
      vid: videoContent,
      vidPreview: videoPreview,
      videoThumbnail: imagePreview,
      visitorId: this.fingerprint.visitorId,
    }

    if (categoryId >= 0) payload.categoryId = categoryId

    return this.#callBackend('getJWT', payload)
  }

  uploadImagesBeforePostCreation = async (id, images) => {
    const endpoint = `${import.meta.env.VITE_APP_BACKEND_URL}/upload-image?address=${this.auth.user.address}&signature=${this.auth.signature}&type=image&hash=${id}`

    const promises = images.map(x => {
      const form = new FormData()
      form.append('images', x, x.name)

      return this.#apiClient.post(endpoint, form, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    })

    const responses = await Promise.all(promises)
    const errors = responses.some(x => !x?.data?.message)

    return errors === false
  }

  getCurrentUserData = async () =>
    // action is correct
    this.#callBackend('checkSignature', {
      af_id: this.affiliate.foundAffiliateId,
    })

  deleteAccount = async () =>
    this.#callBackend('deleteUser', { visitorId: this.fingerprint.visitorId })

  deleteCategory = async categoryId => this.#callBackend('deleteCategory', { categoryId })

  deleteMedia = async (mediaId, fileType, hash = '') =>
    this.#callBackend('deleteImages', { Id: mediaId, fileType, hash })

  deletePost = async id => this.#callBackend('deleteMeeting', { hash: id }) // action is correct

  getAffiliate = async () => await this.#callBackend('getAffiliate')

  getCategories = async address => this.#callBackend('getCategories', { owner: address })

  getCoinbaseOnrampSessionToken = async () => this.#callBackend('getCoinbaseOnrampSessionToken')

  getFavorites = async () => this.#callBackend('getFavorites')

  getFeedPosts = async cancellationSignal =>
    this.#callBackend(
      'checkSchedule',
      { owner: this.wallet.address, wall: 1 },
      { signal: cancellationSignal }
    )

  getImages = async params => this.#callBackend('getImages', params)

  purchaseOrAccessPost = async post =>
    this.#callBackend('getJWTfromHash', {
      accept: true,
      email: this.auth.user.email,
      hash: post.id,
      id: this.wallet.address,
      name: this.wallet.address,
      nickname: this.auth.user.nickname,
      sub: import.meta.env.VITE_JITSI_DOMAIN,
      visitorId: this.fingerprint.visitorId,
    })

  getNotifications = async roomName => this.#callBackend('getnotification', { room_name: roomName })

  getPaymentStatus = async id => this.#callBackend('getMeetHistoryByRoomName', { room_name: id })

  getPost = async hash => this.#callBackend('getPost', { hash })

  getPurchaseHistory = async () => await this.#callBackend('getPurchasedHistory')

  getRevenueHistory = async () => await this.#callBackend('getMyChannelHistory')

  getTransactionHistory = async () => await this.#callBackend('getAllTransactionHistory')

  getLockBalance = async () => this.#callBackend('getLockBalance')

  getStorePosts = async (address, cancellationSignal) =>
    this.#callBackend('checkSchedule', { owner: address, wall: 0 }, { signal: cancellationSignal })

  getCurrentUser = async () => this.#callBackend('getUser')

  getUserByHandleNicknameOrAddress = async x =>
    this.#callBackend('getUserByNicknameOrAffId', { nickname: x })

  getVideo = async params => this.#callBackend('getVideo', params)

  published = async hash => this.#callBackend('published', { hash })

  sendEvent = async (eventName, params = {}) => {
    try {
      params.eventName = eventName
      params.visitorId = this.fingerprint.visitorId

      if (!this.wallet.address || !this.auth.signature) {
        params.requestId = this.fingerprint.requestId
        // TODO: no need for requestId in fingerprint post anymore from here?
      }

      return this.#callBackend('sendEvent', params)
    } catch (e) {
      console.error('[Api] Failed to send event', e)

      return null
    }
  }

  sendMessage = async message => this.#callBackend('sendMessage', { message })

  subscribeToNotifications = async subscription =>
    this.#apiClient
      .post('subscribe', {
        address: this.wallet.address,
        subscription,
        visitorId: this.fingerprint.visitorId,
      })
      .catch(e => {
        console.error('[Api] Request failed', e)

        return null
      })

  checkGas = async () => this.#callBackend('testAllowance') // action is correct

  toggleFavorite = async address => this.#callBackend('addFavorites', { channel: address })

  tip = async (amount, destination, postId = null) =>
    this.#callBackend('tip', {
      amount: amount.toString(),
      destination: destination,
      id: postId || destination,
      notify:
        this.jitsi.isInitialized &&
        (this.jitsi.post.isMeeting || this.jitsi.post.isLivestreamViaJitsi)
          ? 1
          : 0,
    })

  updateCategory = async ({ description, id, name, published }) =>
    this.#callBackend('updateCategory', {
      data: { categoryId: id, categoryName: name, description, published },
    })

  setJitsiOwner = async (hash, ownerId) => this.#callBackend('updateOwnerId', { hash, ownerId })

  updatePost = async (
    post,
    { categoryId, description, imagePreview, price, published, title, videoPreview }
  ) =>
    this.#callBackend('updatePost', {
      categoryId,
      description,
      hash: post.id,
      price: post.isFree ? 0 : price,
      promotion: published ? 1 : 0,
      room_name: title,
      vidPreview: videoPreview,
      videoThumbnail: imagePreview,
    })

  updateProfile = async ({ about, avatar, cover, nickname }) =>
    this.#callBackend('updateProfile', {
      data: {
        about,
        canBook: true,
        cover,
        explicit: this.auth.user.explicit,
        image: avatar,
        nickname,
        price: this.auth.user.defaultPrice,
        promotion: 10,
        roomname: this.auth.user.defaultRoomName,
      },
    })

  finishRegistration = async ({ about, cover, image, handle, nickname }) =>
    this.#callBackend('updateProfile', {
      data: {
        about,
        canBook: true,
        cover,
        explicit: false,
        handle,
        image,
        nickname,
        price: 0,
        promotion: 10,
      },
    })

  updateSignature = async user =>
    this.#callBackend('updateSignature', {
      email: user.email || this.wallet.address,
      name: user.name || this.wallet.address,
      origin,
      profileImage: user.profileImage,
      typeOfLogin: user.typeOfLogin || 'metamask',
      visitorId: this.fingerprint.visitorId,
    })

  #callBackend = async (action, params = {}, config = {}) =>
    this.#apiClient
      .post(
        'D',
        {
          data: {
            action,
            params: {
              address: this.wallet.address || this.fingerprint.visitorId,
              signature: this.auth.signature,
              ...params,
            },
          },
        },
        config
      )
      .catch(e => {
        console.error('[Api] Request failed', e)

        return isAxiosError(e) ? e.response : null
      })
}
